/* Signal: Quiz popup. One question (where do you fish?) → email to Klaviyo → code applied to the cart. */
(() => {
  const STORE_KEY = 'sg_quiz_v1';
  const SCRATCH_KEY = 'sg_scratch_v1'; // people who already signed up through the scratch popup never see this one
  const KLAVIYO_REVISION = '2026-07-15';
  const read = (k) => { try { return JSON.parse(localStorage.getItem(k)) || {}; } catch (e) { return {}; } };
  const store = {
    get() { return read(STORE_KEY); },
    set(v) { try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch (e) {} },
  };
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const forced = /[?&]quiz=1\b/.test(location.search);
  // ?quiz=1 turns on test mode for the rest of the visit
  const session = {
    get() { try { return sessionStorage.getItem('sg_quiz_test') === '1'; } catch (e) { return false; } },
    set() { try { sessionStorage.setItem('sg_quiz_test', '1'); } catch (e) {} },
  };
  if (forced) session.set();
  const testing = forced || session.get();

  // Views and answers go to one shared anonymous counter profile (same one the scratch popup used),
  // so Viewed / Answered / Signup in Klaviyo give the funnel. Test mode and the theme editor are not counted.
  function track(key, name, props) {
    if (testing || !key || designMode) return;
    try {
      fetch(`https://a.klaviyo.com/client/events?company_id=${encodeURIComponent(key)}`, {
        method: 'POST', keepalive: true,
        headers: { 'content-type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
        body: JSON.stringify({ data: { type: 'event', attributes: {
          properties: Object.assign({ page: location.pathname, device: matchMedia('(pointer: fine)').matches ? 'desktop' : 'mobile' }, props || {}),
          metric: { data: { type: 'metric', attributes: { name } } },
          profile: { data: { type: 'profile', attributes: { anonymous_id: 'preysight-popup-counter' } } },
        } } }),
      }).catch(() => {});
    } catch (e) {}
  }

  function init(root) {
    if (!root || root.dataset.sgReady) return;
    root.dataset.sgReady = '1';
    let cfg;
    try { cfg = JSON.parse(root.querySelector('[data-sg-quiz-config]').textContent); } catch (e) { return; }

    const dialog = root.querySelector('[data-sg-quiz-dialog]');
    const form = root.querySelector('[data-sg-quiz-form]');
    const input = form.querySelector('input[type="email"]');
    const submit = form.querySelector('button[type="submit"]');
    const error = root.querySelector('[data-sg-quiz-error]');
    const pick = root.querySelector('[data-sg-quiz-pick]');
    const bubble = root.querySelector('[data-sg-quiz-bubble]');
    const DAY = 864e5, days = (cfg.days || 7) * DAY;
    const save = (patch) => { if (!designMode) store.set(Object.assign(store.get(), patch)); };
    let opened = false;
    let answer = store.get().a || '';

    const setStep = (step) => { dialog.dataset.step = step; };

    /* ---------- the question ---------- */
    function choose(value, fromClick) {
      answer = value;
      root.querySelectorAll('[data-sg-quiz-answer]').forEach((b) => b.classList.toggle('is-picked', b.dataset.sgQuizAnswer === value));
      if (pick && pick.dataset[value]) pick.textContent = pick.dataset[value];
      if (fromClick) { save({ a: value }); track(cfg.key, 'Quiz Popup Answered', { answer: value }); }
      setStep('form');
      if (matchMedia('(pointer: fine)').matches) setTimeout(() => input.focus({ preventScroll: true }), 50);
    }
    root.querySelectorAll('[data-sg-quiz-answer]').forEach((b) => {
      b.addEventListener('click', () => choose(b.dataset.sgQuizAnswer, true));
    });
    root.querySelector('[data-sg-quiz-back]').addEventListener('click', () => setStep('ask'));

    /* ---------- open / close ---------- */
    function open(trigger) {
      if (dialog.open) return;
      track(cfg.key, 'Quiz Popup Viewed', { trigger: typeof trigger === 'string' ? trigger : 'bubble' });
      opened = true;
      hideBubble();
      if (answer && dialog.dataset.step === 'ask') choose(answer, false); // came back: skip straight to the email step
      if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
    }
    function close(remember = true) {
      if (dialog.open) dialog.close();
      if (remember && dialog.dataset.step !== 'done') { save({ s: 'dismissed', t: Date.now() }); showBubble(); }
    }

    /* ---------- reopen bubble ---------- */
    function showBubble() {
      if (!bubble || !cfg.bubble) return;
      const st = store.get();
      if (!designMode && st.bh && Date.now() - st.bh < days) return;
      bubble.hidden = false;
      void bubble.offsetWidth;
      bubble.classList.add('is-in');
    }
    function hideBubble() {
      if (!bubble) return;
      bubble.classList.remove('is-in');
      bubble.hidden = true;
    }
    if (bubble) {
      bubble.querySelector('[data-sg-quiz-reopen]').addEventListener('click', open);
      bubble.querySelector('[data-sg-quiz-bubble-close]').addEventListener('click', () => { save({ bh: Date.now() }); hideBubble(); });
    }
    root.querySelector('[data-sg-quiz-close]').addEventListener('click', () => close());
    dialog.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
    dialog.addEventListener('click', (e) => { if (e.target === dialog) close(); });

    /* ---------- submit to Klaviyo ---------- */
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = input.value.trim();
      error.hidden = true;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        error.textContent = 'Please enter a valid email address.'; error.hidden = false; input.focus(); return;
      }
      if (!cfg.key || !cfg.list) {
        error.textContent = 'Not connected to Klaviyo yet. Add the public key and list ID in the theme editor.'; error.hidden = false; return;
      }
      submit.disabled = true; submit.textContent = 'Unlocking…';
      const props = { signup_source: 'quiz_popup', fishing_water: answer || 'unknown', popup_prize: cfg.prize, popup_code: cfg.code };
      try {
        const res = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(cfg.key)}`, {
          method: 'POST',
          headers: { 'content-type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
          body: JSON.stringify({ data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Quiz popup',
              profile: { data: { type: 'profile', attributes: {
                email, properties: props, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              } } },
            },
            relationships: { list: { data: { type: 'list', id: cfg.list } } },
          } }),
        });
        if (!res.ok) throw new Error(`Klaviyo ${res.status}`);
        // the subscription call can drop custom properties, so tag the profile and log a countable event separately
        const kHeaders = { 'content-type': 'application/vnd.api+json', revision: KLAVIYO_REVISION };
        const kQuery = `?company_id=${encodeURIComponent(cfg.key)}`;
        fetch(`https://a.klaviyo.com/client/profiles${kQuery}`, {
          method: 'POST', headers: kHeaders,
          body: JSON.stringify({ data: { type: 'profile', attributes: { email, properties: props } } }),
        }).catch(() => {});
        fetch(`https://a.klaviyo.com/client/events${kQuery}`, {
          method: 'POST', headers: kHeaders,
          body: JSON.stringify({ data: { type: 'event', attributes: {
            properties: props,
            metric: { data: { type: 'metric', attributes: { name: 'Quiz Popup Signup' } } },
            profile: { data: { type: 'profile', attributes: { email } } },
          } } }),
        }).catch(() => {});
        try {
          if (window.klaviyo && typeof window.klaviyo.identify === 'function') window.klaviyo.identify({ email });
          else { window._learnq = window._learnq || []; window._learnq.push(['identify', { $email: email }]); }
        } catch (err) {}
        success();
      } catch (err) {
        console.warn('[sg-quiz]', err);
        error.textContent = 'Something went wrong. Please try again.'; error.hidden = false;
        submit.disabled = false; submit.textContent = submit.dataset.label;
      }
    });

    function success() {
      setStep('done');
      hideBubble();
      if (!designMode) store.set({ s: 'subscribed', a: answer, t: Date.now() });
      if (cfg.code) {
        const code = encodeURIComponent(cfg.code);
        const base = cfg.root.replace(/\/$/, '');
        fetch(`${base}/discount/${code}`, { credentials: 'same-origin' }).catch(() => {});
        const shop = root.querySelector('[data-sg-quiz-shop]');
        shop.href = `${base}/discount/${code}?redirect=${encodeURIComponent(cfg.shopPath)}`;
      }
      document.dispatchEvent(new CustomEvent('sg:quiz:subscribed', { detail: { answer } }));
    }

    const copyBtn = root.querySelector('[data-sg-quiz-copy]');
    copyBtn.addEventListener('click', () => {
      const done = () => { copyBtn.textContent = 'Copied'; setTimeout(() => { copyBtn.textContent = 'Copy'; }, 1800); };
      if (navigator.clipboard) navigator.clipboard.writeText(cfg.code).then(done, done); else done();
    });

    /* ---------- triggers ---------- */
    if (designMode) {
      document.addEventListener('shopify:section:select', (e) => { if (e.target.contains(root)) open(); });
      document.addEventListener('shopify:section:deselect', (e) => { if (e.target.contains(root)) close(false); });
      return;
    }
    if (forced) { open(); return; }

    const st = store.get();
    if ((!cfg.enabled || !cfg.ready) && !testing) return;
    if (st.s === 'subscribed' || read(SCRATCH_KEY).s === 'subscribed') return;
    if (st.s === 'dismissed' && Date.now() - (st.t || 0) < days) { showBubble(); return; }
    if (cfg.isCart) return;

    const busy = () => !!document.querySelector('dialog[open]') || document.body.classList.contains('overflow-hidden') ||
      document.documentElement.classList.contains('sg-drawer-open') || !!document.querySelector('cart-drawer.is-open, .menu-drawer-container[open], details[open] > .menu-drawer');
    const tryOpen = (trigger) => { if (opened) return; if (busy()) { setTimeout(() => tryOpen(trigger), 4000); return; } open(trigger); };
    setTimeout(() => tryOpen('timer'), Math.max(0, cfg.delay || 0) * 1000);
    if (cfg.exit && matchMedia('(pointer: fine)').matches) {
      document.addEventListener('mouseout', function onOut(e) {
        if (e.relatedTarget || e.clientY > 0) return;
        document.removeEventListener('mouseout', onOut);
        tryOpen('exit');
      });
    }
  }

  const boot = () => document.querySelectorAll('[data-sg-quiz]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', (e) => { const r = e.target.querySelector('[data-sg-quiz]'); if (r) init(r); });
})();
